import React, { useState } from 'react';
import type { IndicatorInput, HexColor } from '@einhaus/project314-types';
import type { IndicatorInputWithChartSettings } from './ChartContainer.tsx';
import { assignColorsToIndicator } from '@/utils/chartHelpers/assignColorsToIndicator.ts';
import { FormSelect, FormNumberInput, Button, DeleteButton } from '../UI';

interface IndicatorSelectorProps {
    onAddIndicator: (indicator: IndicatorInputWithChartSettings) => void;
    onRemoveIndicator: (indicatorId: string) => void;
    activeIndicators: IndicatorInputWithChartSettings[];
}

interface IndicatorOption {
    type: IndicatorInput['type'];
    name: string;
    defaultPeriod: number;
    requiresMultipleInputs?: boolean;
    hasColors?: boolean;
    defaultColors?: HexColor[];
    fastPeriod?: number;
    slowPeriod?: number;
    signalPeriod?: number;
    lookback?: number;
    kPeriod?: number;
    dPeriod?: number;
    rsiPeriod?: number;
    stochasticPeriod?: number;
    tenkanPeriod?: number;
    kijunPeriod?: number;
    senkouBPeriod?: number;
    shift?: number;
    multiplier?: number;
    emaPeriod?: number;
    atrPeriod?: number;
}

export const INDICATOR_OPTIONS: IndicatorOption[] = [
    { type: 'sma', name: 'Simple Moving Average', defaultPeriod: 10 },
    { type: 'ema', name: 'Exponential Moving Average', defaultPeriod: 9 },
    {
        type: 'macd',
        name: 'MACD',
        defaultPeriod: 26,
        fastPeriod: 12,
        signalPeriod: 9,
        requiresMultipleInputs: true,
        hasColors: true,
        defaultColors: ['#2196F3', '#FF5252', '#4CAF50']
    },
    {
        type: 'bollinger',
        name: 'Bollinger Bands',
        defaultPeriod: 20,
        multiplier: 2,
        hasColors: true,
        defaultColors: ['#FF5252', '#2196F3', '#FF5252']
    },
    { type: 'rsi', name: 'Relative Strength Index', defaultPeriod: 14 },
    {
        type: 'stochastic',
        name: 'Stochastic Oscillator',
        defaultPeriod: 14,
        kPeriod: 3,
        dPeriod: 3,
        requiresMultipleInputs: true,
        hasColors: true,
        defaultColors: ['#2196F3', '#FF5252']
    },
    {
        type: 'stochasticrsi',
        name: 'Stochastic RSI',
        defaultPeriod: 14,
        stochasticPeriod: 14,
        kPeriod: 3,
        dPeriod: 3,
        requiresMultipleInputs: true,
        hasColors: true,
        defaultColors: ['#2196F3', '#FF5252']
    },
    {
        type: 'ichimoku',
        name: 'Ichimoku Cloud',
        defaultPeriod: 9,
        kijunPeriod: 26,
        senkouBPeriod: 52,
        shift: 26,
        requiresMultipleInputs: true,
        hasColors: true,
        defaultColors: ['#FF5252', '#2196F3', '#4CAF50', '#FFC107', '#9C27B0']
    },
    { type: 'adx', name: 'Average Directional Index', defaultPeriod: 14 },
    { type: 'atr', name: 'Average True Range', defaultPeriod: 14 },
    { type: 'lwti', name: 'LWTI', defaultPeriod: 4 },
    { type: 'vma', name: 'Volume Moving Average', defaultPeriod: 20 },
    {
        type: 'vwap',
        name: 'VWAP (Intraday Cumulative)',
        defaultPeriod: 0,
        hasColors: true,
        defaultColors: ['#2196F3', '#FFA726', '#FFA726', '#FF5252', '#FF5252']
    },
    { type: 'vwma', name: 'VWMA (Volume Weighted MA)', defaultPeriod: 14 },
    {
        type: 'keltner',
        name: 'Keltner Channel',
        defaultPeriod: 20,
        emaPeriod: 20,
        atrPeriod: 10,
        multiplier: 2,
        requiresMultipleInputs: true,
        hasColors: true,
        defaultColors: ['#FF5252', '#2196F3', '#FF5252']
    }
];

const IndicatorSelector: React.FC<IndicatorSelectorProps> = ({ onAddIndicator, onRemoveIndicator, activeIndicators }) => {
    const [selectedType, setSelectedType] = useState<IndicatorInput['type']>('sma');
    const [period, setPeriod] = useState<number>(20);
    const [fastPeriod, setFastPeriod] = useState<number>(12);
    const [signalPeriod, setSignalPeriod] = useState<number>(9);
    const [kPeriod, setKPeriod] = useState<number>(3);
    const [dPeriod, setDPeriod] = useState<number>(3);
    const [multiplier, setMultiplier] = useState<number>(2);
    const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
    const [usedColors, setUsedColors] = useState<HexColor[]>([]);

    const selectedOption = INDICATOR_OPTIONS.find((option) => option.type === selectedType);

    React.useEffect(() => {
        if (selectedOption) {
            setPeriod(selectedOption.defaultPeriod);
            if (selectedOption.fastPeriod) setFastPeriod(selectedOption.fastPeriod);
            if (selectedOption.kPeriod) setKPeriod(selectedOption.kPeriod);
            if (selectedOption.dPeriod) setDPeriod(selectedOption.dPeriod);
            if (selectedOption.signalPeriod) setSignalPeriod(selectedOption.signalPeriod);
            if (selectedOption.multiplier) setMultiplier(selectedOption.multiplier);
        }
    }, [selectedType, selectedOption]);

    const handleAddIndicator = () => {
        if (!selectedOption) return;

        let newIndicator: IndicatorInput;

        switch (selectedType) {
            case 'sma':
                newIndicator = { type: 'sma', period };
                break;
            case 'ema':
                newIndicator = { type: 'ema', period };
                break;
            case 'macd':
                newIndicator = { type: 'macd', fastPeriod, slowPeriod: period, signalPeriod };
                break;
            case 'bollinger':
                newIndicator = { type: 'bollinger', period, multiplier };
                break;
            case 'rsi':
                newIndicator = { type: 'rsi', period };
                break;
            case 'stochastic':
                newIndicator = { type: 'stochastic', lookback: period, kPeriod, dPeriod };
                break;
            case 'stochasticrsi':
                newIndicator = {
                    type: 'stochasticrsi',
                    rsiPeriod: period,
                    stochasticPeriod: period,
                    kPeriod,
                    dPeriod
                };

                break;
            case 'ichimoku':
                newIndicator = {
                    type: 'ichimoku',
                    tenkanPeriod: period,
                    kijunPeriod: selectedOption.kijunPeriod || 26,
                    senkouBPeriod: selectedOption.senkouBPeriod || 52,
                    shift: selectedOption.shift || 26
                };

                break;
            case 'adx':
                newIndicator = { type: 'adx', period };
                break;
            case 'atr':
                newIndicator = { type: 'atr', period };
                break;
            case 'lwti':
                newIndicator = { type: 'lwti', period };
                break;
            case 'vma':
                newIndicator = { type: 'vma', period };
                break;
            case 'vwap':
                newIndicator = { type: 'vwap' };
                break;
            case 'vwma':
                newIndicator = { type: 'vwma', period };
                break;
            case 'donchian':
                newIndicator = { type: 'donchian', period };
                break;
            case 'keltner':
                newIndicator = {
                    type: 'keltner',
                    emaPeriod: period,
                    atrPeriod: selectedOption.atrPeriod || 10,
                    multiplier
                };

                break;
            default:
                newIndicator = { type: 'sma', period };
        }

        const colors = assignColorsToIndicator(newIndicator, usedColors);
        setUsedColors((prev) => [...prev, ...colors]);

        const newIndicatorWithChartSettings: IndicatorInputWithChartSettings = {
            indicator: newIndicator,
            colors,
            id: `${selectedType}_${Date.now()}`
        };

        onAddIndicator(newIndicatorWithChartSettings);
        setIsFormOpen(false);
    };

    const getPeriodLabel = (): string => {
        switch (selectedType) {
            case 'macd':
                return 'Slow Period';
            case 'stochastic':
                return 'Lookback';
            case 'stochasticrsi':
                return 'RSI Period';
            case 'ichimoku':
                return 'Tenkan Period';
            case 'keltner':
                return 'EMA Period';
            case 'sma':
            case 'ema':
            case 'rsi':
            case 'bollinger':
            case 'adx':
            case 'atr':
            case 'lwti':
            case 'vma':
            case 'vwap':
            case 'vwma':
            case 'donchian':
                return 'Period';
        }
    };

    return (
        <div className="rounded-lg border border-zinc-700 bg-[#1a1a1a] p-3">
            {/* Header */}
            <div className="flex items-center justify-between pb-2.5 mb-3 border-b border-zinc-700">
                <span className="text-sm font-medium text-zinc-300">Technical Indicators</span>
                <Button size="sm" variant="dark" onClick={() => setIsFormOpen(!isFormOpen)}>
                    {isFormOpen ? 'Cancel' : '+ Add'}
                </Button>
            </div>

            {/* Add indicator form */}
            {isFormOpen && (
                <div className="mb-3 rounded-md bg-zinc-800/50 p-3">
                    <div className="form-group mb-3">
                        <label className="text-secondary mb-2" htmlFor="indicator-type">
                            Indicator Type
                        </label>
                        <FormSelect
                            id="indicator-type"
                            options={INDICATOR_OPTIONS.map((option) => ({
                                value: option.type,
                                label: option.name
                            }))}
                            value={selectedType}
                            onChange={(e) => {
                                const selected = e.target.value as IndicatorInput['type'];
                                setSelectedType(selected);
                            }}
                        />
                    </div>

                    {selectedType !== 'vwap' && (
                        <div className="form-group mb-3">
                            <label className="text-secondary mb-2" htmlFor="indicator-period">
                                {getPeriodLabel()}
                            </label>
                            <FormNumberInput
                                integer
                                id="indicator-period"
                                min={1}
                                value={period}
                                onValueChange={(value) => {
                                    // Periods always have a value, so an empty field keeps the current one.
                                    if (value === null) return;

                                    setPeriod(value);
                                }}
                            />
                        </div>
                    )}

                    {selectedType === 'macd' && (
                        <>
                            <div className="form-group mb-3">
                                <label className="text-secondary mb-2" htmlFor="fast-period">
                                    Fast Period
                                </label>
                                <FormNumberInput
                                    integer
                                    id="fast-period"
                                    min={1}
                                    value={fastPeriod}
                                    onValueChange={(value) => {
                                        // Periods always have a value, so an empty field keeps the current one.
                                        if (value === null) return;

                                        setFastPeriod(value);
                                    }}
                                />
                            </div>
                            <div className="form-group mb-3">
                                <label className="text-secondary mb-2" htmlFor="signal-period">
                                    Signal Period
                                </label>
                                <FormNumberInput
                                    integer
                                    id="signal-period"
                                    min={1}
                                    value={signalPeriod}
                                    onValueChange={(value) => {
                                        // Periods always have a value, so an empty field keeps the current one.
                                        if (value === null) return;

                                        setSignalPeriod(value);
                                    }}
                                />
                            </div>
                        </>
                    )}

                    {(selectedType === 'stochastic' || selectedType === 'stochasticrsi') && (
                        <>
                            <div className="form-group mb-3">
                                <label className="text-secondary mb-2" htmlFor="k-period">
                                    K Period
                                </label>
                                <FormNumberInput
                                    integer
                                    id="k-period"
                                    min={1}
                                    value={kPeriod}
                                    onValueChange={(value) => {
                                        // Periods always have a value, so an empty field keeps the current one.
                                        if (value === null) return;

                                        setKPeriod(value);
                                    }}
                                />
                            </div>
                            <div className="form-group mb-3">
                                <label className="text-secondary mb-2" htmlFor="d-period">
                                    D Period
                                </label>
                                <FormNumberInput
                                    integer
                                    id="d-period"
                                    min={1}
                                    value={dPeriod}
                                    onValueChange={(value) => {
                                        // Periods always have a value, so an empty field keeps the current one.
                                        if (value === null) return;

                                        setDPeriod(value);
                                    }}
                                />
                            </div>
                        </>
                    )}

                    {(selectedType === 'bollinger' || selectedType === 'keltner') && (
                        <div className="form-group mb-3">
                            <label className="text-secondary mb-2" htmlFor="multiplier">
                                {selectedType === 'keltner' ? 'ATR Multiplier' : 'Standard Deviation Multiplier'}
                            </label>
                            <FormNumberInput
                                id="multiplier"
                                min={0.1}
                                step={0.1}
                                value={multiplier}
                                onValueChange={(value) => {
                                    // The multiplier always has a value, so an empty field keeps the current one.
                                    if (value === null) return;

                                    setMultiplier(value);
                                }}
                            />
                        </div>
                    )}

                    {selectedType === 'keltner' && (
                        <div className="form-group mb-3">
                            <label className="text-secondary mb-2" htmlFor="atr-period">
                                ATR Period
                            </label>
                            <FormNumberInput
                                integer
                                id="atr-period"
                                min={1}
                                value={period}
                                onValueChange={(value) => {
                                    // Periods always have a value, so an empty field keeps the current one.
                                    if (value === null) return;

                                    setPeriod(value);
                                }}
                            />
                        </div>
                    )}

                    <Button variant="dark" onClick={handleAddIndicator}>
                        Add {selectedOption?.name}
                    </Button>
                </div>
            )}

            {/* Active indicators list */}
            {activeIndicators.length > 0 && (
                <div className="mt-3">
                    <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Active</div>
                    <ul className="flex flex-wrap gap-2">
                        {activeIndicators.map((indicatorWithSettings) => {
                            const { indicator, id, colors } = indicatorWithSettings;
                            const indicatorOption = INDICATOR_OPTIONS.find((opt) => opt.type === indicator.type);

                            const displayPeriod =
                                'period' in indicator
                                    ? indicator.period
                                    : 'slowPeriod' in indicator
                                      ? indicator.slowPeriod
                                      : 'lookback' in indicator
                                        ? indicator.lookback
                                        : 'tenkanPeriod' in indicator
                                          ? indicator.tenkanPeriod
                                          : 'rsiPeriod' in indicator
                                            ? indicator.rsiPeriod
                                            : undefined;

                            return (
                                <li className="flex items-center gap-2 rounded-md border border-zinc-700 bg-zinc-800 px-3 py-2" key={id}>
                                    <span className="flex items-center gap-2 text-sm text-zinc-200">
                                        <span className="flex items-center mr-1">
                                            {Array.isArray(colors) &&
                                                colors.map((color, index) => (
                                                    <span
                                                        className="inline-block w-3 h-3 rounded-full mr-0.5"
                                                        key={`${id}-color-${index}`}
                                                        style={{ backgroundColor: color }}
                                                        title={`Indicator color ${index + 1}`}
                                                    />
                                                ))}
                                        </span>
                                        {indicatorOption?.name || indicator.type}
                                        {displayPeriod !== undefined ? ` (${displayPeriod})` : ''}
                                    </span>
                                    <DeleteButton
                                        showText={false}
                                        size="sm"
                                        title="Remove indicator"
                                        onClick={() => onRemoveIndicator(id)}
                                    />
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            {activeIndicators.length === 0 && !isFormOpen && (
                <div className="text-sm text-zinc-500">No indicators active. Click &quot;+ Add&quot; to get started.</div>
            )}
        </div>
    );
};

export default IndicatorSelector;
